import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";

import ForgotPassword from "./Components/Auth/ForgotPassword";
import ResetPassword from "./Components/Auth/ResetPassword";
import SetPassword from "./Components/Auth/SetPassword";
import SignIn from "./Components/Auth/SignIn";
import AppLayout from "./Components/Layout/AppLayout";
import {
  UnAuthenticatedRoute,
  AuthenticatedRoute,
  PlatformAuthenticatedRoute,
} from "./utils/Route";

import Studies from "./Components/Studies/Studies";
// import Survey from "./Components/Survey/Survey";
import ChangePassword from "./Components/Auth/ChangePassword";
import Error404 from "./Components/Layout/404";
import CustomSignIn from "./Components/Auth/CustomSignIn";
// import WelcomePage from "./Components/Survey/WelcomePage";
// import ThankYouPage from "./Components/Survey/ThankYouPage";
import SurveyWrapper from "./Components/Survey/SurveyWrapper";
import ThankYouWrapper from "./Components/Survey/ThankYouWrapper";
import QuestionPrintPhase from "./Components/QuestionBuilder/QuestionPrint/QuestionPrintPhase";
import QuestionPrintForm from "./Components/QuestionBuilder/QuestionPrint/QuestionPrintForm";
import Organisations from "./Components/Organisations/Organisations";
import CreateOrganisation from "./Components/Organisations/CreateOrganisation";
import ViewOrganisation from "./Components/Organisations/ViewOrganisation/ViewOrganisation";
import Features from "./Components/Features/Features";
import OrganisationSettings from "./Components/OrganisationAdmin/Settings/Settings";
import OrganisationUsers from "./Components/OrganisationAdmin/Users/Users";
import OrganisationUserDetails from "./Components/OrganisationAdmin/Users/OrganisationUserDetails";
import OTPVerification from "./Components/Auth/OTPVerification";
import UserRegistration from "./Components/Auth/UserRegistration";
import PlatformUsers from "./Components/Platform/Users/Users";
import { platformAdminPermissions } from "./utils/roles";
import PrintResponse from "./Components/Survey/PrintResponse/PrintResponse";
import Instruments from "./Components/Platform/Instruments/Instruments";
import AllStudies from "./Components/Studies/AllStudies";
import PlatformConfigTabs from "./Components/PlatformFeatures/PlatformConfigTabs";
import { ParticipantJoinCode } from "./Routes/ParticipantsRoutes";
import Home from "./Home";
import LinkParticipant from "./LinkParticipant";

const routes = createBrowserRouter([
  {
    path: "/auth",
    element: (
      <UnAuthenticatedRoute>
        <Outlet />
      </UnAuthenticatedRoute>
    ),
    children: [
      {
        path: "login",
        element: <SignIn />,
      },
      {
        path: "login/:slug",
        element: <CustomSignIn />,
      },
      {
        path: "register/:slug",
        element: <UserRegistration />,
      },
      {
        path: "forgot-password",
        element: <ForgotPassword />,
      },
      {
        path: "reset-password/:token",
        element: <ResetPassword />,
      },
      {
        path: "signup",
        element: <SetPassword />,
      },
      {
        path: "2fa",
        element: <OTPVerification />,
      },
      {
        path: "participant/login",
        element: <ParticipantJoinCode />,
      },
    ],
  },
  {
    path: "/profile/change-password",
    element: <ChangePassword />,
  },
  {
    path: "/home",
    element: <Home />,
  },
  {
    path: "/app",
    element: (
      <AuthenticatedRoute>
        <AppLayout />
      </AuthenticatedRoute>
    ),
    children: [
      {
        path: "studies",
        element: <Studies />,
      },
      {
        path: "organisation",
        children: [
          {
            index: true,
            element: <Organisations />,
          },
          {
            path: "builder/:id",
            element: <CreateOrganisation />,
          },
          {
            path: "view/:id",
            element: <ViewOrganisation />,
          },
        ],
      },
      {
        path: "features",
        element: (
          <PlatformAuthenticatedRoute
            action={platformAdminPermissions.viewPlatform}
          >
            <Features />
          </PlatformAuthenticatedRoute>
        ),
      },
      {
        path: "settings/:id",
        element: <OrganisationSettings />,
      },
      {
        path: "users",
        children: [
          {
            index: true,
            element: <OrganisationUsers />,
          },
          {
            path: "details/:id",
            element: <OrganisationUserDetails />,
          },
        ],
      },
      {
        path: "platform-users",
        element: (
          <PlatformAuthenticatedRoute
            action={platformAdminPermissions.viewPlatform}
          >
            <PlatformUsers />
          </PlatformAuthenticatedRoute>
        ),
      },
      {
        path: "all-studies",
        children: [
          {
            index: true,
            element: (
              <PlatformAuthenticatedRoute
                action={platformAdminPermissions.managePlatform}
              >
                <AllStudies />
              </PlatformAuthenticatedRoute>
            ),
          },
          {
            path: "study/:id",
            element: (
              <PlatformAuthenticatedRoute
                action={platformAdminPermissions.managePlatform}
              >
                <PlatformConfigTabs />
              </PlatformAuthenticatedRoute>
            ),
          },
        ],
      },

      {
        path: "artifacts",
        element: (
          <PlatformAuthenticatedRoute
            action={platformAdminPermissions.viewPlatform}
          >
            <Instruments />
          </PlatformAuthenticatedRoute>
        ),
      },
    ],
  },
  {
    path: "surveys/:surveySlug",
    element: <SurveyWrapper />,
  },
  {
    path: "surveys/:surveySlug/thank-you",
    element: <ThankYouWrapper />,
  },
  {
    path: "404",
    element: <Error404 />,
  },
  {
    path: "print/study/:id/phase/:type",
    element: (
      <AuthenticatedRoute>
        <QuestionPrintPhase />
      </AuthenticatedRoute>
    ),
  },
  {
    path: "print/study/:id/form/:formId",
    element: (
      <AuthenticatedRoute>
        <QuestionPrintForm />
      </AuthenticatedRoute>
    ),
  },
  {
    path: "print/response",
    element: <PrintResponse />,
  },
  {
    path: "/studies/:id",
    lazy: async () => {
      const { StudyLayoutComp } = await import("./Routes/StudyRoutes");
      return { Component: StudyLayoutComp };
    },
    children: [
      {
        path: "dashboard",
        lazy: async () => {
          const { DashboardComp } = await import("./Routes/StudyRoutes");
          return { Component: DashboardComp };
        },
      },
      {
        path: "study-designer",
        children: [
          {
            index: true,
            lazy: async () => {
              const { StudyDesignerComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: StudyDesignerComp };
            },
          },
          {
            path: "form/:formId/edit",
            lazy: async () => {
              const { QuestionBuilderComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: QuestionBuilderComp };
            },
          },
          {
            path: "form/:formId/edit-translation",
            lazy: async () => {
              const { QuestionBuilderTranslationComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: QuestionBuilderTranslationComp };
            },
          },
          {
            path: "form/:formId/preview",
            lazy: async () => {
              const { QuestionPreviewComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: QuestionPreviewComp };
            },
          },
        ],
      },
      {
        path: "participants",
        children: [
          {
            index: true,
            lazy: async () => {
              const { RecordsComp } = await import("./Routes/StudyRoutes");
              return { Component: RecordsComp };
            },
          },
          {
            path: ":participantId",
            children: [
              {
                path: "details",
                lazy: async () => {
                  const { ParticipantDetailsComp } = await import(
                    "./Routes/StudyRoutes"
                  );
                  return { Component: ParticipantDetailsComp };
                },
              },
              {
                path: "timeline",
                lazy: async () => {
                  const { ParticipantTimelineComp } = await import(
                    "./Routes/StudyRoutes"
                  );
                  return { Component: ParticipantTimelineComp };
                },
              },
              {
                path: "consent-form/:consentId",
                lazy: async () => {
                  const { ConsentFormComp } = await import(
                    "./Routes/StudyRoutes"
                  );
                  return { Component: ConsentFormComp };
                },
              },
              {
                path: "responses",
                lazy: async () => {
                  const { ResponsesComp } = await import(
                    "./Routes/StudyRoutes"
                  );
                  return { Component: ResponsesComp };
                },
              },
              {
                path: "analysis",
                lazy: async () => {
                  const { StatisticsComp } = await import(
                    "./Routes/StudyRoutes"
                  );
                  return { Component: StatisticsComp };
                },
              },
              {
                path: "prescription",
                lazy: async () => {
                  const { PrescriptionComp } = await import(
                    "./Routes/StudyRoutes"
                  );
                  return { Component: PrescriptionComp };
                },
              },
            ],
          },
        ],
      },
      {
        path: "participant-groups",
        lazy: async () => {
          const { ParticipantGroupsComp } = await import(
            "./Routes/StudyRoutes"
          );
          return { Component: ParticipantGroupsComp };
        },
      },
      {
        path: "e-consent",
        children: [
          {
            index: true,
            lazy: async () => {
              const { EConsentComp } = await import("./Routes/StudyRoutes");
              return { Component: EConsentComp };
            },
          },
          {
            path: "form/:formId/edit",
            lazy: async () => {
              const { ConsentBuilderComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: ConsentBuilderComp };
            },
          },

          {
            path: "form/:formId/preview",
            lazy: async () => {
              const { ConsentPreviewComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: ConsentPreviewComp };
            },
          },
          {
            path: "form/:formId/providerSign",
            lazy: async () => {
              const { ConsentPreviewComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: ConsentPreviewComp };
            },
          },
        ],
      },
      {
        path: "medication",
        lazy: async () => {
          const { MedicationComp } = await import("./Routes/StudyRoutes");
          return { Component: MedicationComp };
        },
      },
      {
        path: "audit-trail",
        lazy: async () => {
          const { AuditTrailComp } = await import("./Routes/StudyRoutes");
          return { Component: AuditTrailComp };
        },
      },
      {
        path: "export",
        lazy: async () => {
          const { ExportComp } = await import("./Routes/StudyRoutes");
          return { Component: ExportComp };
        },
      },
      {
        path: "econsents-tracking",
        lazy: async () => {
          const { ConsentsTrackingComp } = await import("./Routes/StudyRoutes");
          return { Component: ConsentsTrackingComp };
        },
      },
      {
        path: "medication",
        lazy: async () => {
          const { MedicationComp } = await import("./Routes/StudyRoutes");
          return { Component: MedicationComp };
        },
      },
      {
        path: "study-settings",
        lazy: async () => {
          const { StudySettingsComp } = await import("./Routes/StudyRoutes");
          return { Component: StudySettingsComp };
        },
      },
      {
        path: "study-theme",
        lazy: async () => {
          const { StudyThemePanelComp } = await import("./Routes/StudyRoutes");
          return { Component: StudyThemePanelComp };
        },
      },
      {
        path: "users",
        children: [
          {
            index: true,
            lazy: async () => {
              const { UsersComp } = await import("./Routes/StudyRoutes");
              return { Component: UsersComp };
            },
          },
          {
            path: ":userId",
            lazy: async () => {
              const { UserDetailComp } = await import("./Routes/StudyRoutes");
              return { Component: UserDetailComp };
            },
          },
        ],
      },

      {
        path: "form-permissions",
        lazy: async () => {
          const { FormPermissionsComp } = await import("./Routes/StudyRoutes");
          return { Component: FormPermissionsComp };
        },
      },
      {
        path: "permissions",
        lazy: async () => {
          const { RolesListComp } = await import("./Routes/StudyRoutes");
          return { Component: RolesListComp };
        },
      },
      {
        path: "queries",
        lazy: async () => {
          const { QueriesComp } = await import("./Routes/StudyRoutes");
          return { Component: QueriesComp };
        },
      },
      {
        path: "appointments",
        children: [
          {
            index: true,
            lazy: async () => {
              const { AppointmentsComp } = await import("./Routes/StudyRoutes");
              return { Component: AppointmentsComp };
            },
          },
          {
            path: "list",
            lazy: async () => {
              const { AppointmentListViewComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: AppointmentListViewComp };
            },
          },
        ],
      },
      {
        path: "my-availability",
        lazy: async () => {
          const { MyAvailabilityComp } = await import("./Routes/StudyRoutes");
          return { Component: MyAvailabilityComp };
        },
      },

      {
        path: "messages",
        lazy: async () => {
          const { MessagesComp } = await import("./Routes/StudyRoutes");
          return { Component: MessagesComp };
        },
      },
      {
        path: "exercise",
        children: [
          {
            index: true,
            lazy: async () => {
              const { ExerciseCollectionComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: ExerciseCollectionComp };
            },
          },
          {
            path: ":exerciseId/categories",
            lazy: async () => {
              const { ExerciseCollectionDetailsComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: ExerciseCollectionDetailsComp };
            },
          },
          {
            path: ":exerciseId/categories/:categoryId/sections",
            lazy: async () => {
              const { SectionBuilderComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: SectionBuilderComp };
            },
          },
          {
            path: "builder",
            lazy: async () => {
              const { ExerciseBuilderComp } = await import(
                "./Routes/StudyRoutes"
              );
              return { Component: ExerciseBuilderComp };
            },
          },
        ],
      },
    ],
  },
  {
    path: "/participant",
    lazy: async () => {
      const { ParticipantLayoutComp } = await import(
        "./Routes/ParticipantsRoutes"
      );
      return { Component: ParticipantLayoutComp };
    },
    children: [
      {
        path: "home",
        lazy: async () => {
          const { HomeComp } = await import("./Routes/ParticipantsRoutes");
          return { Component: HomeComp };
        },
      },
      {
        path: "questionnaires",
        lazy: async () => {
          const { QuestionnaireComp } = await import(
            "./Routes/ParticipantsRoutes"
          );
          return { Component: QuestionnaireComp };
        },
      },
      {
        path: "questionnaires/:id/survey/:surveyId",
        lazy: async () => {
          const { SurveyComp } = await import("./Routes/ParticipantsRoutes");
          return { Component: SurveyComp };
        },
      },
      {
        path: "e-consent",
        children: [
          {
            index: true,
            lazy: async () => {
              const { EConsentComp } = await import(
                "./Routes/ParticipantsRoutes"
              );
              return { Component: EConsentComp };
            },
          },
        ],
      },
      {
        path: "appointments",
        lazy: async () => {
          const { ParticipantAppointmentsComp } = await import(
            "./Routes/ParticipantsRoutes"
          );
          return { Component: ParticipantAppointmentsComp };
        },
      },
    ],
  },
  {
    path: "sign/:id",
    lazy: async () => {
      const { SignConsent } = await import("./Routes/ParticipantsRoutes");
      return { Component: SignConsent };
    },
  },
  {
    path: "link/participant/redirect/:type",
    element: <LinkParticipant />,
  },
  {
    path: "/telehealth/:studyId/:apptId",
    lazy: async () => {
      const { OpenTokComp } = await import("./Routes/StudyRoutes");
      return { Component: OpenTokComp };
    },
  },
  {
    path: "/",
    element: <Navigate to="/home" replace />,
  },
  {
    path: "*",
    element: <Navigate to="/home" replace />,
  },
]);

export default routes;
